import { fabric } from 'fabric';
import { cloneFabricObject } from '@frontend/lib/fabric/effects/helpers/clone-handler';
import { fxCanvas } from '@frontend/lib/glfx';

export class ProjectedShadowEffect {
  static _SHADOW_PADDING_PERCENT = 5;
  
  static isActive(obj) {
    return obj?.shadow?.projection?.enabled && !obj.angle;
  }

  static apply(obj, ctx) {
    if (!obj?.shadow?.projection) return;

    if (!obj.shadow.projection._normalized) {
      this._initProjection(obj);
    }

    const {
      modifiedCoords,
      normalizedSkew,
      canvasHeight, 
      canvasWidth, 
      offsetX,
      offsetY, 
      elementWidth, 
      elementHeight, 
      blur, 
      color,
    } = this._buildData(obj);

    const tempCanvas = this._buildTempCanvas({ obj, elementWidth, canvasWidth, canvasHeight, offsetX, offsetY })
    this._applyFade(tempCanvas, { offsetY, elementHeight, fade: obj.shadow.projection.fade });
    const fxCanvas = this._buildFxCanvas(tempCanvas, { modifiedCoords, offsetX, offsetY, elementWidth, elementHeight });
    const finalCanvas = this._buildFinalCanvas(fxCanvas, { blur, color });

    ctx.drawImage(
      finalCanvas,
      -offsetX - blur - (elementWidth / 2),
      -offsetY - blur - (elementHeight / 2)
    );

    this._recalculateShadowCoords(obj, normalizedSkew);
  }

  static _normalizeSkew(obj) {
    if (!obj || !obj.shadow || !obj.shadow.projection) return;

    const proj = obj.shadow.projection;

    if (!Array.isArray(proj.value) || proj.value.length !== 8) {
      proj.value = [0,0,0,0,0,0,0,0];
      proj._normalized = true;
      return;
    }

    if (proj._normalized) return;

    const maybeAbsolute = proj.value.some(v => Math.abs(v) > 2);

    if (!maybeAbsolute) {
      proj._normalized = true;
      return;
    }

    const originalSize = obj.getOriginalSize 
      ? obj.getOriginalSize() 
      : { 
        width: obj.getScaledWidth(), 
        height: obj.getScaledHeight() 
      };
    const w = originalSize.width || obj.getScaledWidth();
    const h = originalSize.height || obj.getScaledHeight();

    const converted = proj.value.map((v, i) => {
      const isX = i % 2 === 0;
      const size = isX ? w : h;
      return size ? v / size : 0;
    });

    proj.value = converted;
    proj._normalized = true;
  }

  static _denormalizeSkew(normalizedSkew, elementWidth, elementHeight) {
    if (!Array.isArray(normalizedSkew) || normalizedSkew.length !== 8) {
      return [0,0,0,0,0,0,0,0];
    }
    return normalizedSkew.map((v, i) => {
      const isX = i % 2 === 0;
      const size = isX ? elementWidth : elementHeight;
      return v * size;
    });
  }

  static _recalculateShadowCoords(obj, normalizedSkew) {
    if (!obj) return;

    const w = obj.getScaledWidth();
    const h = obj.getScaledHeight();

    const skewNorm = Array.isArray(normalizedSkew) && normalizedSkew.length === 8 ? normalizedSkew : [0,0,0,0,0,0,0,0];

    const modifiedScaled = skewNorm.map((v, i) => {
      const isX = i % 2 === 0;
      const size = isX ? w : h;
      return (isX ? 0 : 0) + v * size;
    });

    const originalScaled = [0, 0, w, 0, w, h, 0, h];

    const final = originalScaled.map((base, i) => base + modifiedScaled[i]);

    obj.shadowCoords = {
      tl: { x: final[0] + obj.left, y: final[1] + obj.top },
      tr: { x: final[2] + obj.left, y: final[3] + obj.top },
      br: { x: final[4] + obj.left, y: final[5] + obj.top },
      bl: { x: final[6] + obj.left, y: final[7] + obj.top }
    };
  }
  
  // Fades the silhouette top-to-bottom before the perspective transform:
  // the projected far edge fades out, the anchored base stays opaque.
  static _applyFade(canvas, { offsetY, elementHeight, fade }) {
    const fadeAmount = Math.min(100, Math.max(0, Number(fade) || 0)) / 100;
    if (!fadeAmount) return;

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    const gradient = ctx.createLinearGradient(0, offsetY, 0, offsetY + elementHeight);
    gradient.addColorStop(0, `rgba(0, 0, 0, ${1 - fadeAmount})`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  static _initProjection(obj) {
    if (!obj?.shadow?.projection) return;

    obj.shadow.projection.value = [0,0,0,0,0,0,0,0];
    obj.shadow.projection._normalized = true;
  }

  static _buildData(obj) {
    const normalizedSkew = obj.shadow.projection.value;
    const originalSize = obj.getOriginalSize
      ? obj.getOriginalSize()
      : {
        width: obj.width || obj.getScaledWidth(),
        height: obj.height || obj.getScaledHeight()
      };
    const elementWidth = originalSize.width;
    const elementHeight = originalSize.height;
    const blur = obj.shadow.blur;
    const color = obj.shadow.color;
    const skew = this._denormalizeSkew(normalizedSkew, elementWidth, elementHeight);

    const originalBoxCoords = [
      0, 0,
      elementWidth, 0,
      elementWidth, elementHeight,
      0, elementHeight
    ];

    const modifiedCoords = originalBoxCoords.map((v, i) => v + skew[i]);

    const xCoords = [modifiedCoords[0], modifiedCoords[2], modifiedCoords[4], modifiedCoords[6]];
    const yCoords = [modifiedCoords[1], modifiedCoords[3], modifiedCoords[5], modifiedCoords[7]];

    const minX = Math.min(0, ...xCoords);
    const maxX = Math.max(elementWidth, ...xCoords);
    const minY = Math.min(0, ...yCoords);
    const maxY = Math.max(elementHeight, ...yCoords);

    const offsetX = minX < 0 ? -minX : 0;
    const offsetY = minY < 0 ? -minY : 0;

    const baseWidth = maxX - minX;
    const baseHeight = maxY - minY;

    const scaleFactor = Math.max(baseWidth / obj.getScaledWidth(), baseHeight / obj.getScaledHeight());
    const adjustedBlur = blur * scaleFactor / 2;

    const canvasWidth = Math.ceil(baseWidth + adjustedBlur * 2);
    const canvasHeight = Math.ceil(baseHeight + adjustedBlur * 2);
    
    return {
      blur: adjustedBlur,
      color: color,
      modifiedCoords,
      normalizedSkew,
      canvasWidth,
      canvasHeight,
      elementWidth,
      elementHeight,
      offsetX,
      offsetY,
    }
  }

  static _buildTempCanvas({ obj, canvasWidth, canvasHeight, elementWidth, offsetX, offsetY }) {
    const shadowPadding = this._SHADOW_PADDING_PERCENT * elementWidth / 100;

    const tempCanvas = new fabric.StaticCanvas(null, {
      width: canvasWidth + shadowPadding * 2,
      height: canvasHeight + shadowPadding * 2,
      enableRetinaScaling: false,
    });

    const elementData = obj.toObject();

    elementData.left = shadowPadding - elementWidth;
    elementData.top = shadowPadding + offsetY;

    elementData.shadow = {
      ...elementData.shadow,
      color: '#000000',
      blur: 0,
      projection: null,
      offsetX: elementWidth + offsetX,
      offsetY: 0,
    };
    elementData.originX = 'left';
    elementData.originY = 'top';
    elementData.scaleX = 1;
    elementData.scaleY = 1;

    const clone = cloneFabricObject(obj, elementData);
    tempCanvas.add(clone);
    tempCanvas.renderAll();
    
    const cleanBBox = {
      x: shadowPadding,
      y: shadowPadding,
      width: canvasWidth,
      height: canvasHeight,
    };

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cleanBBox.width;
    croppedCanvas.height = cleanBBox.height;

    const ctx = croppedCanvas.getContext('2d');
    ctx.drawImage(
      tempCanvas.getElement(),
      cleanBBox.x,
      cleanBBox.y,
      cleanBBox.width,
      cleanBBox.height,
      0,
      0,
      cleanBBox.width,
      cleanBBox.height
    );

    tempCanvas.dispose();

    return croppedCanvas;
  }  
  
  static _buildFxCanvas(tempCanvas, { modifiedCoords, offsetX, offsetY, elementWidth, elementHeight }) {
    const originalCoords = [
      offsetX, offsetY,
      offsetX + elementWidth, offsetY,
      offsetX + elementWidth, offsetY + elementHeight,
      offsetX, offsetY + elementHeight
    ];

    const adjustedModifiedCoords = [
      modifiedCoords[0] + offsetX, modifiedCoords[1] + offsetY,
      modifiedCoords[2] + offsetX, modifiedCoords[3] + offsetY,
      modifiedCoords[4] + offsetX, modifiedCoords[5] + offsetY,
      modifiedCoords[6] + offsetX, modifiedCoords[7] + offsetY,
    ];

    const texture = fxCanvas.texture(tempCanvas);
    fxCanvas.draw(texture).perspective(originalCoords, adjustedModifiedCoords).update();
    texture.destroy();
    
    return fxCanvas;
  }

  static _buildFinalCanvas(fxCanvas, shadowProps) {
    const { blur, color } = shadowProps;
    
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = fxCanvas.width;
    finalCanvas.height = fxCanvas.height;
    const finalCtx = finalCanvas.getContext('2d');

    finalCtx.shadowBlur = blur;
    finalCtx.shadowColor = color;
    finalCtx.shadowOffsetX = fxCanvas.width + blur;
    finalCtx.shadowOffsetY = blur;

    finalCtx.drawImage(fxCanvas, -fxCanvas.width, 0);
    
    return finalCanvas;
  }
}

export const initProjectedShadowControls = () => {
  const OBJECTS = [fabric.Object, fabric.Textbox];

  // Object-local offset so handle coords are correct for any originX/originY.
  const getOriginOffset = (fabricObject) => {
    const width = fabricObject.getScaledWidth();
    const height = fabricObject.getScaledHeight();
    return {
      offsetX:
        fabricObject.originX === 'center' ? -width / 2
          : fabricObject.originX === 'right' ? -width : 0,
      offsetY:
        fabricObject.originY === 'center' ? -height / 2
          : fabricObject.originY === 'bottom' ? -height : 0,
    };
  };

  // Shared skeleton for every projection drag handle. Per-handle config:
  //   getPoint(coords)        -> { x, y } in object-local space
  //   applyDelta(value,px,py) -> mutates the 8-entry skew array
  //   drawGuide(ctx, obj, left, top) -> optional extra rendering
  const createProjectionControl = ({ getPoint, applyDelta, cursor, drawGuide }) =>
    new fabric.Control({
      actionName: 'shadow-projection',
      getVisibility(fabricObject, controlKey) {
        const objectVisibility = fabricObject._controlsVisibility;
        if (objectVisibility && typeof objectVisibility[controlKey] !== 'undefined') {
          return objectVisibility[controlKey];
        }
        const shadowProjection = fabricObject.shadow?.projection;
        return shadowProjection && shadowProjection.edit;
      },
      positionHandler(dim, finalMatrix, fabricObject) {
        const zoom = fabricObject.canvas ? fabricObject.canvas.getZoom() : 1;
        const coords = fabricObject.shadowCoords;
        if (!coords) return { x: 0, y: 0 };

        const { offsetX, offsetY } = getOriginOffset(fabricObject);
        const point = getPoint(coords);

        return {
          x: (point.x + offsetX) * zoom,
          y: (point.y + offsetY) * zoom,
        };
      },
      actionHandler(eventData, transform, x, y) {
        const fabricObject = transform.target;
        if (!fabricObject?.shadow?.projection) return false;

        ProjectedShadowEffect._normalizeSkew(fabricObject);

        if (transform.lastX === undefined || transform.lastY === undefined) {
          transform.lastX = x;
          transform.lastY = y;
          return true;
        }

        const dxPx = x - transform.lastX;
        const dyPx = y - transform.lastY;

        transform.lastX = x;
        transform.lastY = y;

        if (Math.abs(dxPx) < 0.5 && Math.abs(dyPx) < 0.5) {
          return true;
        }

        const visualW = fabricObject.getScaledWidth();
        const visualH = fabricObject.getScaledHeight();

        const px = visualW ? dxPx / visualW : 0;
        const py = visualH ? dyPx / visualH : 0;

        const proj = fabricObject.shadow.projection;
        if (!Array.isArray(proj.value) || proj.value.length !== 8) {
          proj.value = [0, 0, 0, 0, 0, 0, 0, 0];
        }

        applyDelta(proj.value, px, py);
        proj._normalized = true;

        ProjectedShadowEffect._recalculateShadowCoords(fabricObject, proj.value);

        fabricObject.canvas.fire("shadow:changed", fabricObject);
        fabricObject.canvas.requestRenderAll();
        return true;
      },
      render(ctx, left, top, styleOverride, fabricObject) {
        if (!fabricObject.shadowCoords) return;

        // currentScaleVal (from ZoomService) keeps handles a constant size.
        const scale = window.currentScaleVal || 1;
        const scaledCornerSize = fabricObject.cornerSize * scale;

        ctx.save();
        ctx.fillStyle = 'red';
        ctx.beginPath();
        ctx.arc(left, top, scaledCornerSize / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();

        if (drawGuide) drawGuide(ctx, fabricObject, left, top);
      },
      cursorStyleHandler() {
        return cursor;
      },
    });

  // Corner handles draw a guide line back to the shadow centre.
  const drawCornerGuide = (ctx, fabricObject, left, top) => {
    const zoom = fabricObject.canvas ? fabricObject.canvas.getZoom() : 1;
    const coords = fabricObject.shadowCoords;
    const { offsetX, offsetY } = getOriginOffset(fabricObject);

    const center = {
      x: ((coords.tl.x + coords.tr.x + coords.br.x + coords.bl.x) / 4 + offsetX) * zoom,
      y: ((coords.tl.y + coords.tr.y + coords.br.y + coords.bl.y) / 4 + offsetY) * zoom,
    };

    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 1 / zoom;
    ctx.beginPath();
    ctx.moveTo(center.x, center.y);
    ctx.lineTo(left, top);
    ctx.stroke();
    ctx.restore();
  };

  const CORNER_SKEW_INDEX = { tl: [0, 1], tr: [2, 3], br: [4, 5], bl: [6, 7] };

  const controlConfigs = {};

  // Four corner handles — each drives one corner of the skew array.
  Object.entries(CORNER_SKEW_INDEX).forEach(([corner, [ix, iy]]) => {
    controlConfigs[`${corner}_shadow-projection`] = {
      getPoint: (coords) => coords[corner],
      applyDelta: (value, px, py) => {
        value[ix] += px;
        value[iy] += py;
      },
      cursor: 'pointer',
      drawGuide: drawCornerGuide,
    };
  });

  // Middle handle — drags both top corners together to adjust shadow height.
  controlConfigs['tm_shadow-projection'] = {
    getPoint: (coords) => ({
      x: (coords.tl.x + coords.tr.x) / 2,
      y: (coords.tl.y + coords.tr.y) / 2,
    }),
    applyDelta: (value, px, py) => {
      value[0] += px;
      value[1] += py;
      value[2] += px;
      value[3] += py;
    },
    cursor: 'ns-resize',
  };

  OBJECTS.forEach(X => {
    Object.entries(controlConfigs).forEach(([key, config]) => {
      X.prototype.controls[key] = createProjectionControl(config);
    });
  });
};
